import { EComboItemType } from "../../Enum/EComboItemType"
import { AddtionalEntity } from "../../Objects/Addtional/AddtionalEntity"
import { AwardEntity } from "../../Objects/Awards/AwardEntity"
import { CategoryEntity } from "../../Objects/Category/CategoryEntity"
import { ComboEntity } from "../../Objects/Combo/ComboEntity"
import { CustomerEntity } from "../../Objects/Customer/CustomerEntity"
import { DrinkEntity } from "../../Objects/MealsAndDrinks/DrinkEntity"
import { MealEntity } from "../../Objects/MealsAndDrinks/MealEntity"
import { Promotions } from "../../Objects/Promotion/Promotions"
import { SelectionEntity } from "../../Objects/Selection/SelectionEntity"
import { AssociationItem } from "./AssociationsPopup"

const getGenericFoodTitle = (name: string): string => `${name}`

export const resolvePromotionAssociations = (
  promotion: Promotions,
  meals: MealEntity[],
  drinks: DrinkEntity[],
  combos: ComboEntity[],
): AssociationItem[] => {
  const items: AssociationItem[] = []

  meals.forEach((meal) => {
    if (meal.genericFoodEntity.promotionsIds.includes(promotion.id)) {
      items.push({ id: meal.id, title: getGenericFoodTitle(meal.genericFoodEntity.name), badge: "Refeição" })
    }
  })

  drinks.forEach((drink) => {
    if (drink.genericFoodEntity.promotionsIds.includes(promotion.id)) {
      items.push({ id: drink.id, title: getGenericFoodTitle(drink.genericFoodEntity.name), badge: "Bebida" })
    }
  })

  combos.forEach((combo) => {
    if (combo.genericFoodEntity.promotionsIds.includes(promotion.id)) {
      items.push({ id: combo.id, title: getGenericFoodTitle(combo.genericFoodEntity.name), badge: "Combo" })
    }
  })

  return items
}

export const resolveAwardAssociations = (award: AwardEntity, customers: CustomerEntity[]): AssociationItem[] => {
  return customers
    .filter(
      (customer) =>
        customer.awardsIds.includes(award.id) || customer.awardManagers.some((manager) => manager.awardId === award.id),
    )
    .map((customer) => ({
      id: customer.id,
      title: customer.personEntity.name,
      subtitle: `CPF: ${customer.personEntity.cpf || "-"}`,
      badge: "Cliente",
    }))
}

export const resolveCategoryAssociations = (
  category: CategoryEntity,
  meals: MealEntity[],
  drinks: DrinkEntity[],
  combos: ComboEntity[],
): AssociationItem[] => {
  const items: AssociationItem[] = []

  meals.forEach((meal) => {
    if (meal.genericFoodEntity.categoryId === category.id) {
      items.push({ id: meal.id, title: meal.genericFoodEntity.name, badge: "Refeição" })
    }
  })

  drinks.forEach((drink) => {
    if (drink.genericFoodEntity.categoryId === category.id) {
      items.push({ id: drink.id, title: drink.genericFoodEntity.name, badge: "Bebida" })
    }
  })

  combos.forEach((combo) => {
    if (combo.genericFoodEntity.categoryId === category.id) {
      items.push({ id: combo.id, title: combo.genericFoodEntity.name, badge: "Combo" })
    }
  })

  return items
}

export const resolveSelectionAssociations = (
  selection: SelectionEntity,
  meals: MealEntity[],
  drinks: DrinkEntity[],
  combos: ComboEntity[],
): AssociationItem[] => {
  const hasSelection = (ids: number[]) => ids.includes(selection.id)

  const items: AssociationItem[] = []

  meals.forEach((meal) => {
    if (hasSelection(meal.genericFoodEntity.allowedSelections.map((e) => e.id))) {
      items.push({ id: meal.id, title: meal.genericFoodEntity.name, badge: "Refeição" })
    }
  })

  drinks.forEach((drink) => {
    if (hasSelection(drink.genericFoodEntity.allowedSelections.map((e) => e.id))) {
      items.push({ id: drink.id, title: drink.genericFoodEntity.name, badge: "Bebida" })
    }
  })

  combos.forEach((combo) => {
    if (hasSelection(combo.genericFoodEntity.allowedSelections.map((e) => e.id))) {
      items.push({ id: combo.id, title: combo.genericFoodEntity.name, badge: "Combo" })
    }
  })

  return items
}

export const resolveAdditionalAssociations = (
  additional: AddtionalEntity,
  meals: MealEntity[],
  drinks: DrinkEntity[],
  combos: ComboEntity[],
): AssociationItem[] => {
  const hasAdditional = (ids: number[]) => ids.includes(additional.id)

  const items: AssociationItem[] = []

  meals.forEach((meal) => {
    const ids = [
      ...meal.genericFoodEntity.allowedAddtionals.map((e) => e.id),
      ...meal.genericFoodEntity.freeAllowedAddtionals.map((e) => e.id),
    ]
    if (hasAdditional(ids)) {
      items.push({ id: meal.id, title: meal.genericFoodEntity.name, badge: "Refeição" })
    }
  })

  drinks.forEach((drink) => {
    const ids = [
      ...drink.genericFoodEntity.allowedAddtionals.map((e) => e.id),
      ...drink.genericFoodEntity.freeAllowedAddtionals.map((e) => e.id),
    ]
    if (hasAdditional(ids)) {
      items.push({ id: drink.id, title: drink.genericFoodEntity.name, badge: "Bebida" })
    }
  })

  combos.forEach((combo) => {
    const ids = [
      ...combo.genericFoodEntity.allowedAddtionals.map((e) => e.id),
      ...combo.genericFoodEntity.freeAllowedAddtionals.map((e) => e.id),
    ]
    if (hasAdditional(ids)) {
      items.push({ id: combo.id, title: combo.genericFoodEntity.name, badge: "Combo" })
    }
  })

  return items
}

export const resolveDrinkComboAssociations = (drink: DrinkEntity, combos: ComboEntity[]): AssociationItem[] => {
  return combos
    .filter((combo) => combo.items.some((item) => item.itemType === EComboItemType.Drink && item.itemId === drink.id))
    .map((combo) => ({ id: combo.id, title: combo.genericFoodEntity.name, badge: "Combo" }))
}

export const resolveMealComboAssociations = (meal: MealEntity, combos: ComboEntity[]): AssociationItem[] => {
  return combos
    .filter((combo) => combo.items.some((item) => item.itemType === EComboItemType.Meal && item.itemId === meal.id))
    .map((combo) => ({ id: combo.id, title: combo.genericFoodEntity.name, badge: "Combo" }))
}
